import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Checkbox, IconButton, Menu, MenuItem, TableCell, TableRow } from "@mui/material";
import { serializeDateTime } from "@notemeal/utils-date-time";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";
import { TWTooltip } from "apps/web/src/componentLibrary/TWTooltip/TWTooltip";
import { TWTableActions } from "apps/web/src/componentLibrary/TWTableActions";
import { AiAugmentedMenuItemName } from "apps/web/src/components/MenuItem/Dialog/AiAugmentedMenuItemName";
import { onCreate, onEdit } from "apps/web/src/components/MenuItem/Dialog/utils";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { Score } from "shared-ui/Score/Score";
import { ConfirmationDialog } from "../../../../componentLibrary";
import MenuItemCreateDialog from "../../../../components/MenuItem/Dialog/MenuItemDialog";
import { MenuItemState } from "../../../../components/MenuItem/reducer";
import TablePage from "../../../../components/universal/TablePage";
import TablePageDefaultHeader from "../../../../components/universal/TablePage/DefaultHeader";
import {
  MenuItemTableFragment,
  useCreateReusableMenuItemMutation,
  useDeleteReusableMenuItemMutation,
  useBulkDeleteReusableMenuItemsMutation,
  useEditReusableMenuItemMutation,
  useEditReusableMenuItemPartialMutation,
  useMenuItemsTableQuery,
  useMyScoringSystemQuery,
} from "../../../../types";
import { useOffsetPagination } from "../../../../utils/pagination";
import MenuItemEditDialog, { OnSaveMenuItemArgs } from "../../../../views/MenuItems/Dialog/Edit/MenuItemEditDialog";
import { MenuItemDuplicateDialog } from "../../../../views/MenuItems/Dialog/MenuItemDuplicateDialog";
import MenuItemRatingsDialog from "../../../../views/MenuItems/Dialog/MenuItemRatings/MenuItemRatingsDialog";
import { getAverageRating, getNumberOfRatings } from "../../../../views/MenuItems/Dialog/MenuItemRatings/utils";
import { useNutritionFlags } from "../../../../hooks/useNutritionFlags";
import { getOrgMenuItemNavigation } from "./KitchenPaths";

interface MoreInfo {
  moreAnchorElement: HTMLElement;
  menuItem: MenuItemTableFragment;
}

export const MenuItemsPage = () => {
  const { isBulkDeleteMenuItemsEnabled, isRecipeAndMenuItemRedesignEnabled } = useNutritionFlags();
  const { formatDateWithLocale } = useDateFormatting();
  const navigate = useNavigate();
  const [moreInfo, setMoreInfo] = useState<MoreInfo | null>(null);
  const [selectedMenuItemIds, setSelectedMenuItemIds] = useState<Set<string>>(new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);

  const paginationHooks = useOffsetPagination();
  const { query, limit, offset, queryText, onChangeQueryText } = paginationHooks;

  const {
    data,
    loading: loadingMenuItems,
    refetch,
  } = useMenuItemsTableQuery({
    variables: { query, input: { limit, offset } },
    fetchPolicy: "network-only",
  });
  const rows = data?.menuItemOffsetConnection.edges ?? [];

  const { data: scoringSystemData, loading: loadingScoringSystem } = useMyScoringSystemQuery();
  const scoringSystem = scoringSystemData?.myScoringSystem;
  const hasScoring = scoringSystem !== null;

  const { setMessage } = useSnackbar();

  const [duplicateMenuItemId, setDuplicateMenuItemId] = useState<string | null>(null);
  const [displayCreateMenuItemDialog, setDisplayCreateMenuItemDialog] = useState(false);
  const pendingCreateMenuItemNameRef = useRef("");
  const [createReusableMenuItem, { loading: savingCreate }] = useCreateReusableMenuItemMutation({
    onError: () => {
      setMessage("error", `Unable to create ${pendingCreateMenuItemNameRef.current} as a menu item`);
    },
    onCompleted: () => {
      refetch();
      setMessage("success", `Created menu item`);
      setDisplayCreateMenuItemDialog(false);
      setDuplicateMenuItemId(null);
    },
  });
  const handleCreate = (state: MenuItemState) => {
    pendingCreateMenuItemNameRef.current = state.menuItem.name;
    onCreate({ state, createReusableMenuItem });
  };

  const [editMenuItemId, setEditMenuItemId] = useState<string | null>(null);
  const [editReusableMenuItem, { loading: savingEdit }] = useEditReusableMenuItemMutation({
    onError: e => {
      if (e.message.includes("duplicate key value violates unique constraint")) {
        setMessage(
          "error",
          "We cannot save the new revision of this menu item at this time since it is outdated. Please close the edit dialog and try again."
        );
      } else {
        setMessage("error", "Failed to save, please try again.");
      }
    },
    onCompleted: () => {
      refetch();
      setEditMenuItemId(null);
      setMessage("success", `Edited menu item`);
    },
  });
  const [editReusableMenuItemPartial, { loading: savingEditPartial }] = useEditReusableMenuItemPartialMutation({
    onError: e => setMessage("error", e.message),
    onCompleted: () => {
      refetch();
      setEditMenuItemId(null);
      setMessage("success", `Edited menu item`);
    },
  });

  const handleEdit = (state: OnSaveMenuItemArgs) => {
    onEdit({ editReusableMenuItem, editReusableMenuItemPartial, state });
  };

  const [deleteableMenuItem, setDeleteableMenuItem] = useState<MenuItemTableFragment | null>(null);
  const [deleteReusableMenuItem] = useDeleteReusableMenuItemMutation({
    onCompleted: () => {
      refetch();
      setDeleteableMenuItem(null);
      setMessage("success", `Deleted menu item`);
    },
  });
  const handleDelete = (menuItem: MenuItemTableFragment) => {
    deleteReusableMenuItem({
      variables: {
        input: {
          after: serializeDateTime(new Date()),
          menuItemId: menuItem.id,
        },
      },
    });
  };

  const [bulkDeleteReusableMenuItems] = useBulkDeleteReusableMenuItemsMutation({
    onCompleted: data => {
      refetch();
      setSelectedMenuItemIds(new Set());
      setBulkDeleteConfirmOpen(false);
      const count = data.bulkDeleteReusableMenuItems.menuItemIds.length;
      setMessage("success", `${count} menu item${count === 1 ? "" : "s"} have been deleted`);
    },
    onError: e => setMessage("error", e.message),
  });

  const handleBulkDelete = () => {
    bulkDeleteReusableMenuItems({
      variables: {
        input: {
          after: serializeDateTime(new Date()),
          menuItemIds: [...selectedMenuItemIds]
        },
      },
    });
  };

  const handleSelectRow = (id: string) => {
    setSelectedMenuItemIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const rowIds = rows.map(r => r.id);
    const allSelected = rowIds.every(id => selectedMenuItemIds.has(id));
    if (allSelected) {
      setSelectedMenuItemIds(prev => {
        const next = new Set(prev);
        rowIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedMenuItemIds(prev => new Set([...prev, ...rowIds]));
    }
  };

  const [menuItemForRatings, setMenuItemForRatings] = useState<MenuItemTableFragment | null>(null);

  const allVisibleSelected = rows.length > 0 && rows.every(r => selectedMenuItemIds.has(r.id));
  const someVisibleSelected = rows.some(r => selectedMenuItemIds.has(r.id));

  return (
    <>
      <TablePage
        header={
          <TablePageDefaultHeader
            title="Menu Item"
            queryText={queryText}
            onChangeQueryText={onChangeQueryText}
            onCreate={() => {
              if (isRecipeAndMenuItemRedesignEnabled) {
                const { to, options } = getOrgMenuItemNavigation();
                navigate(to, options);
                return;
              }
              setDisplayCreateMenuItemDialog(true);
            }}
          />
        }
        tableHeaderRow={
          <TableRow>
            {isBulkDeleteMenuItemsEnabled && <TableCell padding="checkbox">
              <Checkbox
                onClick={handleSelectAll}
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected && !allVisibleSelected}
                inputProps={{ "aria-label": "Select all menu items" }}
              />
            </TableCell>}
            <TableCell>Name</TableCell>
            <TableCell>Portion Name</TableCell>
            <TableCell>Average Rating</TableCell>
            <TableCell>Number of Add-ons</TableCell>
            {hasScoring && <TableCell>Score</TableCell>}
            <TableCell>Date Created</TableCell>
            <TableCell>Last Updated</TableCell>
            <TableCell padding="checkbox">Actions</TableCell>
          </TableRow>
        }
        tableBodyRows={
          <>
            {rows.map(row => {
              const {
                id,
                isMissingIngredients,
                usesOldRecipe,
                name,
                servingName,
                createdAt,
                updatedAt,
                history: { ratingCounts },
                choices,
                score,
                aiAugmented,
              } = row;
              const { isAiGenerated, isAiTranscribed } = aiAugmented;
              const isSelected = selectedMenuItemIds.has(id);
              return (
                <TableRow
                  id={id}
                  key={id}
                  hover
                  selected={isSelected}
                  onClick={e => {
                    if (isRecipeAndMenuItemRedesignEnabled) {
                      const { to, options } = getOrgMenuItemNavigation(undefined, id);
                      navigate(to, options);
                      return;
                    }
                    setEditMenuItemId(id);
                  }}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) {
                      return;
                    }
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (isRecipeAndMenuItemRedesignEnabled) {
                        const { to, options } = getOrgMenuItemNavigation(undefined, id);
                        navigate(to, options);
                        return;
                      }
                      setEditMenuItemId(id);
                    }
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: "-2px",
                    }
                  }}
                >
                  {isBulkDeleteMenuItemsEnabled && <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onClick={e => {
                        e.stopPropagation();
                        handleSelectRow(id);
                      }}
                      inputProps={{ "aria-label": `Select ${name}` }}
                    />
                  </TableCell>}
                  <TableCell scope="row">
                    {isMissingIngredients ? (
                      <Box sx={{ display: "flex" }}>
                        <TWTooltip title="" subcopy="Menu item is missing ingredients! This will affect food log data from menu orders.">
                          <WarningIcon fontSize="small" color="warning" />
                        </TWTooltip>
                        <Box sx={{ display: "flex", alignItems: "center", pl: 0.5 }}>{name}</Box>
                      </Box>
                    ) : usesOldRecipe ? (
                      <Box sx={{ display: "flex" }}>
                        <TWTooltip title="" subcopy="Old versions of recipes are being used">
                          <WarningIcon fontSize="small" color="warning" />
                        </TWTooltip>
                        <Box sx={{ display: "flex", alignItems: "center", pl: 0.5 }}>{name}</Box>
                      </Box>
                    ) : isAiGenerated || isAiTranscribed ? (
                      <AiAugmentedMenuItemName
                        name={name}
                        sx={{ fontSize: "14px" }}
                        isAiGenerated={isAiGenerated}
                        isAiTranscribed={isAiTranscribed}
                      />
                    ) : (
                      name
                    )}
                  </TableCell>
                  <TableCell>{servingName}</TableCell>
                  <TableCell>{getNumberOfRatings(ratingCounts) !== 0 && `${getAverageRating(ratingCounts)} / 5`}</TableCell>
                  <TableCell>{choices.length}</TableCell>
                  {hasScoring && (
                    <TableCell>
                      {score && <Score
                        scoreValue={score.value}
                        scoringSystem={scoringSystem}
                        size="lg"
                        sx={{ gap: 0.5 }} />}
                    </TableCell>
                  )}
                  <TableCell>{formatDateWithLocale(new Date(createdAt))}</TableCell>
                  <TableCell>{formatDateWithLocale(new Date(updatedAt))}</TableCell>
                  <TableCell sx={{ display: "flex" }}>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        setMoreInfo({ moreAnchorElement: e.currentTarget, menuItem: row });
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          event.stopPropagation();
                          setMoreInfo({ moreAnchorElement: event.currentTarget, menuItem: row });
                        }
                      }}
                      role="button"
                      aria-label="More Actions"
                      tabIndex={0}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            <Menu
              anchorEl={moreInfo?.moreAnchorElement}
              open={!!moreInfo}
              onClose={() => {
                setMoreInfo(null);
              }}
              keepMounted
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              {moreInfo && (
                <>
                  <MenuItem
                    dense
                    disabled={getNumberOfRatings(moreInfo.menuItem.history.ratingCounts) === 0}
                    onClick={() => {
                      setMenuItemForRatings(moreInfo.menuItem);
                      setMoreInfo(null);
                    }}
                  >
                    View Ratings
                  </MenuItem>
                  <MenuItem
                    dense
                    onClick={() => {
                      if (isRecipeAndMenuItemRedesignEnabled) {
                        const { to, options } = getOrgMenuItemNavigation({
                          duplicatedMenuItemId: moreInfo.menuItem.id,
                          isDuplicate: true,
                        });
                        navigate(to, options);
                      } else {
                        setDuplicateMenuItemId(moreInfo.menuItem.id);
                      }
                      setMoreInfo(null);
                    }}
                  >
                    Duplicate
                  </MenuItem>
                  <MenuItem
                    dense
                    onClick={() => {
                      setDeleteableMenuItem(moreInfo.menuItem);
                      setMoreInfo(null);
                    }}
                  >
                    Delete
                  </MenuItem>
                </>
              )}
            </Menu>
          </>
        }
        paginationHooks={paginationHooks}
        loading={loadingMenuItems || loadingScoringSystem}
        total={data?.menuItemOffsetConnection.pageInfo.total || 0}
      />
      {selectedMenuItemIds.size > 0 && (
        <TWTableActions
          selectedCount={selectedMenuItemIds.size}
          onClearSelection={() => setSelectedMenuItemIds(new Set())}
          actions={[
            {
              id: "delete",
              label: "Delete",
              onClick: () => setBulkDeleteConfirmOpen(true),
            },
          ]}
        />
      )}
      {displayCreateMenuItemDialog && (
        <MenuItemCreateDialog
          open={displayCreateMenuItemDialog}
          title="Create Reusable Menu Item"
          variant="MenuItem"
          onClose={() => setDisplayCreateMenuItemDialog(false)}
          saving={savingCreate}
          onSave={handleCreate}
        />
      )}
      {editMenuItemId && (
        <MenuItemEditDialog
          menuItemId={editMenuItemId}
          open={!!editMenuItemId}
          onClose={() => setEditMenuItemId(null)}
          saving={savingEdit || savingEditPartial}
          onSave={handleEdit}
          onDuplicate={() => setDuplicateMenuItemId(editMenuItemId)}
        />
      )}
      {duplicateMenuItemId && (
        <MenuItemDuplicateDialog
          originalMenuItemId={duplicateMenuItemId}
          open={!!duplicateMenuItemId}
          onClose={() => setDuplicateMenuItemId(null)}
          saving={savingCreate}
          onSave={handleCreate}
        />
      )}
      {menuItemForRatings && (
        <MenuItemRatingsDialog
          menuItem={menuItemForRatings}
          open={!!menuItemForRatings}
          onClose={() => setMenuItemForRatings(null)} />
      )}
      {deleteableMenuItem && (
        <ConfirmationDialog
          open={!!deleteableMenuItem}
          title="Delete Menu Item"
          message="Are you sure that you would like to delete the menu item?"
          onCancel={() => setDeleteableMenuItem(null)}
          onConfirm={() => handleDelete(deleteableMenuItem)}
          variant="containedDestructive"
        />
      )}
      {bulkDeleteConfirmOpen && (
        <ConfirmationDialog
          open={bulkDeleteConfirmOpen}
          title="Delete Menu Items"
          message={`Are you sure that you would like to delete ${selectedMenuItemIds.size} menu item${selectedMenuItemIds.size === 1 ? "" : "s"}?`}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={handleBulkDelete}
          variant="containedDestructive"
        />
      )}
    </>
  );
};
